/**
 * 500's UI module: everything the table screen must be told rather than work
 * out. Labels, captions, the sentence under a seat, the ladder layout and the
 * bodies of the two decisions 500 has (kitty discard, Joker nomination).
 *
 * Adding Belote is another file like this one plus a registry line; `table/`
 * does not change.
 */
import type { ReactNode } from 'react'
import { cardPlayedBy, type CardId, type PendingDecision, type Seat, type Suit } from '@cards/shared'
import type { AuctionSeatCall, DecisionBodyProps, GameUi, LegalCall } from '../types'
import type { Match, TableView } from '../../table/view'
import { Glyph, Strainy, countWord } from '../../table/atoms'
import { cardText } from '../../cards/assets'
import { BidLadder } from './BidLadder'
import { KittyExchange } from './KittyExchange'
import { cardMarksFor, cardPhrase, kittyCardsFor, rankingFor, sortHand as sortFhHand } from './hand'
import {
  isPassCall,
  parseSpecLabel,
  readAuctionCalls,
  readContract,
  readSpec,
  specLabel,
  type ContractView,
  type Spec,
} from './spec'

const TRICKS = 10

/** One inline element, not a fragment: the scorepad's `.k` row is a flex
 *  container with a gap, which would otherwise split `8` from `♥`. */
function SpecText({ spec, glyphSize }: { spec: Spec; glyphSize?: number }): JSX.Element {
  if (spec.kind === 'misere') return <span>Misère</span>
  if (spec.kind === 'openMisere') return <span>Open misère</span>
  return (
    <span>
      {spec.level}
      <Strainy strain={spec.strain} glyphSize={glyphSize} />
    </span>
  )
}

/** The highest bid so far — from the auction's `high`, or failing that the last
 *  non-pass call in the auction log. */
function highBid(view: TableView): ContractView | null {
  const auction = view.hand?.auction as Record<string, unknown> | undefined
  const fromHigh = auction ? readContract(auction.high) : null
  if (fromHigh) return fromHigh
  const calls = readAuctionCalls(auction)
  for (let i = calls.length - 1; i >= 0; i--) {
    const spec = readSpec(calls[i].call)
    if (spec) return { spec, contractor: calls[i].seat, value: 0 }
  }
  return null
}

function isMisere(spec: Spec | null | undefined): boolean {
  return spec?.kind === 'misere' || spec?.kind === 'openMisere'
}

function contractOf(view: TableView): ContractView | null {
  return readContract(view.hand?.contract)
}

function teamTricks(view: TableView, team: number | null): number {
  if (team === null || !view.match) return 0
  let n = 0
  for (const t of view.hand?.tricks ?? []) {
    if (t.winner !== null && view.match.table.teamOf[t.winner] === team) n++
  }
  return n
}

export const fiveHundredUi: GameUi = {
  id: 'five-hundred',
  name: '500',
  rotation: 'clockwise',

  tricksPerHand: (_match: Match | null) => TRICKS,

  tricksNeeded: (match) => {
    const contract = readContract(match?.hand?.contract)
    if (!contract) return null
    return contract.spec.kind === 'suit' ? contract.spec.level : null
  },

  contractReading: (view) => {
    const hand = view.hand
    if (!hand) return null
    const contract = contractOf(view)
    if (contract) {
      const mine = contract.contractor === view.mySeat
      const who = mine ? 'You' : view.name(contract.contractor)
      if (isMisere(contract.spec)) {
        return {
          node: (
            <span style={{ fontSize: 18 }}>
              <SpecText spec={contract.spec} />
              <span className="who">{who}</span>
            </span>
          ),
          caption: 'must lose all ten',
        }
      }
      return {
        node: (
          <>
            <SpecText spec={contract.spec} />
            <span className="who">{who}</span>
          </>
        ),
        caption: mine ? `contract, ${contract.value}` : 'contract',
      }
    }
    const high = highBid(view)
    if (!high) {
      // A hand nobody bid for is not a hand still waiting for a bid.
      const caption = hand.phase === 'abandoned' ? 'passed out' : 'no bid yet'
      return { node: <span className="faint">—</span>, caption }
    }
    return {
      node: (
        <>
          <SpecText spec={high.spec} />
          <span className="who">{high.contractor === view.mySeat ? 'You' : view.name(high.contractor)}</span>
        </>
      ),
      caption: 'bid to beat',
    }
  },

  trackCaption: (view) => {
    const hand = view.hand
    if (!hand) return ''
    const contract = contractOf(view)
    const needed = contract && contract.spec.kind === 'suit' ? contract.spec.level : null

    if (contract && isMisere(contract.spec)) {
      const who = contract.contractor === view.mySeat ? 'You have' : `${view.name(contract.contractor)} has`
      const taken =
        contract.contractor === null ? 0 : (view.seats[contract.contractor]?.tricksWon ?? 0)
      return `${who} taken ${taken}`
    }

    const us = view.trickDots.filter((d) => d === 'us').length
    const them = view.trickDots.filter((d) => d === 'them').length
    if (hand.phase === 'abandoned') return 'no tricks played'
    if (hand.phase === 'handComplete') {
      const mineIsContract = contract && view.match && contract.contractor !== null
        ? view.match.table.teamOf[contract.contractor] === view.myTeam
        : true
      const taken = mineIsContract ? us : them
      return needed === null ? `${taken} tricks` : `${taken} tricks, needed ${needed}`
    }
    if (us + them === 0) return needed === null ? `${TRICKS} tricks to play` : `need ${needed} of ${TRICKS}`
    return needed === null ? `${us} of ${TRICKS}, them ${them}` : `${us} of ${needed}, them ${them}`
  },

  // Both come from the server: `winner` once the trick is settled, `winning`
  // while it is still on the table. The client has no second opinion.
  winningSeat: (view) => {
    const trick = view.trick
    if (!trick || trick.plays.length === 0) return null
    return trick.winner ?? trick.winning ?? null
  },

  seatStatus: (view, seat) => {
    const s = view.seats[seat]
    if (!s) return null
    if (!s.active) return 'sits out'
    const parts: ReactNode[] = []
    if (s.isPartner) parts.push('partner')
    // A bot is the laptop ahead of its name (Felt.tsx), not a word down here.
    if (!s.connected && !s.isBot) parts.push('away')
    const contract = contractOf(view)
    if (contract && isMisere(contract.spec) && contract.contractor === seat) parts.push('contractor')

    const trick = view.trick
    if (trick) {
      if (trick.winner === seat) parts.push('takes the trick')
      else if (trick.winner === null && trick.plays.length > 0) {
        if (trick.winning === seat && !isMisere(contract?.spec)) parts.push('winning')
        else if (trick.leader === seat && trick.ledSuit) {
          parts.push(
            <span key="led">
              led <Glyph suit={trick.ledSuit} />
            </span>,
          )
        }
      } else if (trick.plays.length === 0 && view.previousTrick?.winner === seat) {
        parts.push('leads next')
      }
    }
    if (parts.length === 0) return null
    return (
      <>
        {parts.map((p, i) => (
          <span key={i}>
            {i > 0 ? ', ' : ''}
            {p}
          </span>
        ))}
      </>
    )
  },

  callout: (view) => {
    const trick = view.trick
    if (!trick || trick.plays.length === 0) return null
    const ctx = rankingFor(view)
    const contract = contractOf(view)
    // `winner` is the settled trick, `winning` the one still on the table; the
    // card is then simply the one that seat played.
    const leading = trick.winner ?? trick.winning
    const card = leading === null ? null : cardPlayedBy(trick, leading)
    if (leading === null || card === null) return null
    const best = { seat: leading, card }
    const name = view.name(best.seat)
    const phrase = cardPhrase(best.card, ctx)
    const what: ReactNode = phrase ? phrase : <CardWord card={best.card} />

    if (trick.winner !== null) {
      const needed = contract && contract.spec.kind === 'suit' ? contract.spec.level : TRICKS
      const count =
        view.match && contract && contract.contractor !== null
          ? teamTricks(view, view.match.table.teamOf[contract.contractor])
          : view.trickDots.filter((d) => d === 'us').length
      return (
        <>
          <b>{name}</b> takes it with {what}, <b>{count}</b> of {needed}
        </>
      )
    }
    if (isMisere(contract?.spec)) {
      return (
        <>
          <b>
            {name}'s <CardWord card={best.card} />
          </b>{' '}
          is taking the trick so far
        </>
      )
    }
    return (
      <>
        <b>{name}</b> has it with {what}
      </>
    )
  },

  handHint: (view) => {
    const trick = view.trick
    if (view.isMyTurn) {
      if (trick && trick.plays.length > 0 && trick.ledSuit && view.legalPlays.size < view.myCards.length) {
        return (
          <>
            <b>Your turn</b>, follow <Glyph suit={trick.ledSuit} />
          </>
        )
      }
      if (trick && trick.plays.length === 0) {
        return (
          <>
            <b>Your turn</b>, lead
          </>
        )
      }
      return <b>Your turn</b>
    }
    // The contractor exchanging the kitty is not "to play" yet, and saying so
    // is what made the moment after the auction read as the hand having
    // started without anybody playing.
    const pending = view.hand?.pending
    if (view.hand?.phase === 'decision' && pending) {
      return `Waiting for ${view.name(pending.seat)} ${fiveHundredUi.decisionWaiting(pending.kind)}`
    }
    if (trick && trick.winner !== null) return `${view.name(trick.winner)} leads next`
    if (view.toAct !== null) return `${view.name(view.toAct)} to play`
    return 'Your hand'
  },

  cardMarks: (view, card) => cardMarksFor(card, rankingFor(view), kittyCardsFor(view)),

  sortHand: (view, cards) => sortFhHand(cards, rankingFor(view)),

  handResult: (view) => {
    const hand = view.hand
    const result = hand?.result
    // A passed-out hand has no result to report because nothing happened —
    // which is itself the thing that has to be said, and loudly, since the next
    // screen after it is a fresh deal that would otherwise arrive unexplained.
    if (hand?.phase === 'abandoned') {
      const dealer = hand.dealer
      const next = view.name((dealer + 1) % view.seatCount)
      return {
        headline: 'Thrown in',
        sentence: `Nobody bid, so the hand is dead and the cards go back. ${
          (dealer + 1) % view.seatCount === view.mySeat ? 'You deal' : `${next} deals`
        } the next one.`,
      }
    }
    if (!result || !view.match) return null
    const contract = contractOf(view)
    const contractorTeam =
      contract && contract.contractor !== null ? view.match.table.teamOf[contract.contractor] : null
    const mine = contractorTeam !== null && contractorTeam === view.myTeam
    const made = result.contractMade
    const taken = teamTricks(view, contractorTeam)
    const needed = contract && contract.spec.kind === 'suit' ? contract.spec.level : 0
    const partner = view.seats.find((s) => s.isPartner)

    if (contract && isMisere(contract.spec)) {
      const who = mine ? 'You' : view.name(contract.contractor)
      const took = contract.contractor === null ? 0 : (view.seats[contract.contractor]?.tricksWon ?? 0)
      return {
        headline: made ? (mine ? 'Made it' : 'They made it') : mine ? 'Set' : 'Set them',
        sentence: made
          ? `${who} lost every trick.`
          : `${who} took ${countWord(took)} ${took === 1 ? 'trick' : 'tricks'}, and only had to lose them all.`,
      }
    }

    const diff = Math.abs(taken - needed)
    if (mine) {
      return {
        headline: made ? 'Made it' : 'Set',
        sentence: made
          ? `You and ${partner?.name ?? 'your partner'} took ${countWord(taken)} tricks, ${
              diff === 0 ? 'exactly the bid' : `${countWord(diff)} more than the bid`
            }.`
          : `You and ${partner?.name ?? 'your partner'} took ${countWord(taken)}, ${countWord(diff)} short of ${countWord(needed)}.`,
      }
    }
    const who = view.name(contract?.contractor ?? null) || 'They'
    return {
      headline: made ? 'They made it' : 'Set them',
      sentence: made
        ? `${who} needed ${countWord(needed)} and took ${countWord(taken)}.`
        : `${who} needed ${countWord(needed)} and took ${countWord(taken)}.`,
    }
  },

  contractLabelNode: (label) => {
    const spec = parseSpecLabel(label)
    return spec ? <SpecText spec={spec} /> : <>{label}</>
  },

  auctionRule: 'The auction goes round until three in a row pass.',

  auctionStrip: (view) => {
    const out: (AuctionSeatCall | null)[] = new Array(view.seatCount).fill(null)
    for (const { seat, call } of readAuctionCalls(view.hand?.auction)) {
      const spec = readSpec(call)
      out[seat] = spec
        ? { label: <SpecText spec={spec} glyphSize={16} />, pass: false }
        : { label: 'pass', pass: true }
    }
    // Whoever holds the standing bid is the one everybody else has to beat.
    // `highBid` is the auction's own `high` where the server sends one and the
    // last non-pass call otherwise, so this is not a second opinion about who
    // is winning the auction — it is the same one the status band reads.
    const high = highBid(view)
    const at = high?.contractor ?? null
    const marked = at === null ? null : out[at]
    if (at !== null && marked && !marked.pass) out[at] = { ...marked, high: true }
    return out
  },

  auctionCalls: (view) => {
    const out: AuctionSeatCall[][] = Array.from({ length: view.seatCount }, () => [])
    for (const { seat, call } of readAuctionCalls(view.hand?.auction)) {
      const spec = readSpec(call)
      out[seat]?.push(
        spec ? { label: <SpecText spec={spec} glyphSize={13} />, pass: false } : { label: 'Pass', pass: true },
      )
    }
    // The standing bid is the last call of the seat that holds it — the same
    // `highBid` the status band and the strip read.
    const at = highBid(view)?.contractor ?? null
    const mine = at === null ? undefined : out[at]
    const last = mine && mine.length > 0 ? mine[mine.length - 1] : undefined
    if (mine && last && !last.pass) mine[mine.length - 1] = { ...last, high: true }
    return out
  },

  isPass: (call: LegalCall) => isPassCall(call.call, call.label),

  explainHeadline: (call) => {
    const spec = readSpec(call.call) ?? parseSpecLabel(call.label)
    const value = call.value
    if (!spec) return <>{call.label}</>
    return (
      <>
        <SpecText spec={spec} glyphSize={15} />
        {value === undefined ? null : <> for {value}</>}
      </>
    )
  },

  bidButtonLabel: (call) => {
    const spec = readSpec(call.call) ?? parseSpecLabel(call.label)
    if (!spec) return <>Bid {call.label}</>
    if (spec.kind !== 'suit') {
      return (
        <>
          Bid {specLabel(spec)}
          {call.value === undefined ? null : <> for {call.value}</>}
        </>
      )
    }
    return (
      <>
        Bid {spec.level}
        <Strainy strain={spec.strain} glyphSize={14} />
        {call.value === undefined ? null : <> for {call.value}</>}
      </>
    )
  },

  BidLayout: BidLadder,

  decisionLayout: (kind) => (kind === 'discardKitty' ? 'takeover' : 'sheet'),

  decisionWaiting: (kind) =>
    kind === 'discardKitty' ? 'to exchange the kitty' : kind === 'nominateJoker' ? 'to name a suit' : 'to decide',

  decisionHint: (pending) =>
    pending.kind === 'discardKitty' ? 'Tap a card to choose it. You lead the first trick.' : null,

  DecisionBody,
}

function CardWord({ card }: { card: CardId }): JSX.Element {
  if (card === 'JOKER') return <span>the Joker</span>
  const text = cardText(card)
  const suit = text.slice(-1)
  const glyph: Record<string, Suit> = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' }
  const s = glyph[suit]
  return (
    <span>
      {text.slice(0, -1)}
      {s ? <Glyph suit={s} /> : suit}
    </span>
  )
}

function DecisionBody(props: DecisionBodyProps): JSX.Element {
  if (props.pending.kind === 'discardKitty') return <KittyExchange {...props} />
  if (props.pending.kind === 'nominateJoker') return <JokerNomination {...props} />
  return <GenericDecision {...props} />
}

/** The Joker's suit is chosen before trick 1 in No Trumps and both misères —
 *  the single most bug-prone rule in the game, so the choice is spelled out. */
function JokerNomination({ pending, onSubmit, busy }: DecisionBodyProps): JSX.Element {
  const options = optionsOf(pending)
  return (
    <div className="sheet-body">
      <h2>Nominate the Joker</h2>
      <p className="muted">
        Choose the suit the Joker belongs to for this hand, or leave it floating as the top card of
        the pack.
      </p>
      <div className="option-row">
        {options
          .filter((o) => o.suit)
          .map((option, i) => (
            <button
              key={i}
              className="btn btn-secondary"
              disabled={busy}
              onClick={() => onSubmit(option.decision)}
            >
              <Glyph suit={option.suit!} />
            </button>
          ))}
      </div>
      {options
        .filter((o) => !o.suit)
        .map((option, i) => (
          <button
            key={i}
            className="btn btn-quiet btn-block"
            disabled={busy}
            onClick={() => onSubmit(option.decision)}
          >
            {option.label}
          </button>
        ))}
    </div>
  )
}

function GenericDecision({ pending, onSubmit, busy }: DecisionBodyProps): JSX.Element {
  const options = optionsOf(pending)
  return (
    <div className="sheet-body">
      <h2>{pending.kind}</h2>
      <div className="option-row">
        {options.map((option, i) => (
          <button key={i} className="btn btn-secondary" disabled={busy} onClick={() => onSubmit(option.decision)}>
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

interface DecisionOption {
  readonly label: string
  readonly suit: Suit | null
  readonly decision: unknown
}

/** Option prompts are validated by membership, exactly like calls, so the
 *  chosen option is submitted back verbatim. */
function optionsOf(pending: PendingDecision): DecisionOption[] {
  const payload = pending.payload as Record<string, unknown> | null
  const raw = Array.isArray(payload?.options)
    ? (payload!.options as unknown[])
    : Array.isArray(payload?.suits)
      ? (payload!.suits as unknown[])
      : []
  return raw.map((option) => {
    if (typeof option === 'string') {
      const suit = ['S', 'H', 'D', 'C'].includes(option) ? (option as Suit) : null
      return { label: option, suit, decision: option }
    }
    const rec = (option ?? {}) as Record<string, unknown>
    const suitValue = rec.suit
    const suit =
      typeof suitValue === 'string' && ['S', 'H', 'D', 'C'].includes(suitValue)
        ? (suitValue as Suit)
        : null
    return { label: String(rec.label ?? rec.kind ?? 'None'), suit, decision: option }
  })
}

export type { Seat }
